/**
 * Benutzergruppen mit Rechtematrix.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { permissionSectionsFor, type Permission } from '@erp/shared';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from '../components/toast';
import { Alert, Badge, Dialog, Field, LoadingBlock, TextArea, TextInput } from '../components/ui';

export type Group = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  sortOrder: number;
  active: boolean;
  permissions: Permission[];
  memberCount: number;
};

export function GroupsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState<Group | 'neu' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/api/groups'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Die Gruppe wurde gelöscht.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  if (isLoading || !data) return <LoadingBlock />;

  return (
    <>
      <Alert kind="info">
        Eine Gruppe bündelt Rechte. Ein Benutzer kann in mehreren Gruppen sein — die Rechte
        addieren sich. Einzelne Abweichungen werden direkt am Benutzer gesetzt und haben Vorrang
        vor der Gruppe.
      </Alert>

      <div className="card">
        <div className="card-header">
          <h2>{data.groups.length} Gruppen</h2>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('neu')}>
            Gruppe anlegen
          </button>
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Gruppe</th>
                <th className="numeric">Rechte</th>
                <th className="numeric">Mitglieder</th>
                <th className="shrink">Art</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((group) => (
                <tr key={group.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{group.name}</div>
                    {group.description ? <div className="faint">{group.description}</div> : null}
                  </td>
                  <td className="numeric">{group.permissions.length}</td>
                  <td className="numeric muted">{group.memberCount || ''}</td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      {group.isSystem ? <Badge>Mitgeliefert</Badge> : null}
                      {!group.active ? <Badge kind="warning">Inaktiv</Badge> : null}
                    </div>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button type="button" className="btn btn-sm" onClick={() => setEditing(group)}>
                        Rechte
                      </button>
                      {!group.isSystem ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => remove.mutate(group.id)}
                        >
                          Löschen
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <GroupDialog group={editing === 'neu' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function GroupDialog({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { modules } = useAuth();

  const [name, setName] = useState(group?.name ?? '');
  const [key, setKey] = useState(group?.key ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [active, setActive] = useState(group?.active ?? true);
  const [permissions, setPermissions] = useState<Set<Permission>>(
    new Set(group?.permissions ?? []),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  function toggle(permission: Permission) {
    setPermissions((current) => {
      const next = new Set(current);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }

  function toggleSection(keys: Permission[], enable: boolean) {
    setPermissions((current) => {
      const next = new Set(current);
      for (const entry of keys) {
        if (enable) next.add(entry);
        else next.delete(entry);
      }
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        description: description || null,
        active,
        permissions: [...permissions],
      };
      return group
        ? api.patch(`/api/groups/${group.id}`, payload)
        : api.post('/api/groups', { ...payload, key, sortOrder: 100 });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Gespeichert. Betroffene Benutzer müssen sich neu anmelden.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Speichern fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={group ? `Gruppe ${group.name}` : 'Gruppe anlegen'}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto', fontSize: 12.5 }}>
            {permissions.size} Rechte ausgewählt
          </span>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !name.trim() || (!group && !key.trim())}
            onClick={() => {
              setErrors({});
              save.mutate();
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="form-grid" style={{ marginBottom: 18 }}>
        <Field label="Name" span={group ? 12 : 7} required error={errors.name}>
          <TextInput
            value={name}
            onChange={setName}
            invalid={Boolean(errors.name)}
            disabled={group?.isSystem}
            autoFocus
          />
        </Field>
        {!group ? (
          <Field label="Schlüssel" span={5} required error={errors.key} hint="klein, ohne Leerzeichen">
            <TextInput
              value={key}
              onChange={(value) => setKey(value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              invalid={Boolean(errors.key)}
            />
          </Field>
        ) : null}
        <Field label="Beschreibung" span={12}>
          <TextArea value={description} onChange={setDescription} rows={2} />
        </Field>
      </div>

      {group?.isSystem ? (
        <Alert kind="info">
          Mitgelieferte Gruppe: Name und Schlüssel sind fest, die Rechte können Sie ändern.
        </Alert>
      ) : null}

      {group && group.memberCount > 0 ? (
        <Alert kind="warning">
          {group.memberCount} {group.memberCount === 1 ? 'Benutzer ist' : 'Benutzer sind'} in dieser
          Gruppe. Eine Änderung wirkt sofort — die Betroffenen werden abgemeldet.
        </Alert>
      ) : null}

      {permissionSectionsFor(modules).map((section) => {
        const keys = section.permissions.map((entry) => entry.key);
        const allSet = keys.every((entry) => permissions.has(entry));

        return (
          <fieldset key={section.title} style={{ marginBottom: 14 }}>
            <legend style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <span>{section.title}</span>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => toggleSection(keys, !allSet)}
              >
                {allSet ? 'keine' : 'alle'}
              </button>
            </legend>

            {section.permissions.map((entry) => (
              <label
                key={entry.key}
                className="checkbox-row"
                style={{ padding: '3px 0', alignItems: 'flex-start' }}
              >
                <input
                  type="checkbox"
                  checked={permissions.has(entry.key)}
                  onChange={() => toggle(entry.key)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  {entry.label}
                  <span className="faint" style={{ display: 'block', fontSize: 12 }}>
                    {entry.description}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        );
      })}

      {group && !group.isSystem ? (
        <label className="checkbox-row">
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
          Aktiv — eine inaktive Gruppe vergibt keine Rechte mehr
        </label>
      ) : null}
    </Dialog>
  );
}
